import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import {
    CALCULATOR_FIELD_UNITS,
    CalculatorService,
    ICalculatorField,
    ICalculatorSection,
} from '../../services';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-calculator-box',
    templateUrl: './calculator-box.component.html',
    styleUrls: ['./calculator-box.component.scss']
})
export class CalculatorBoxComponent implements OnInit, OnDestroy {
    @Input() title!: string; // Title of the box
    @Input() form!: FormGroup; // The form group all the fields of this box should belong to
    @Input() sections!: ICalculatorSection[];
    @Input() valueChange!: () => void; // Callback for when the input is updated
    @Input() isCentered?: boolean; // If boxes is used as only box in row, it can be centered
    @Input() isLeft?: boolean; // If boxes is used on left side, different styling is applied.
    @Input() isRefinance?: boolean = false; // If this box is the SA Rental Refinance box
    @Output() refinanceModeSwitch: EventEmitter<any> = new EventEmitter<any>(); // Emits when the refinanceMode switches from cash to finance

    public refinanceChecked: boolean = false;
    public disableRefinanceSwitch: boolean = false;
    public lastInteraction: Date = new Date();
    public disregardNextBlur = false;
    private refinanceModeSubscription$: Subscription | undefined;

    constructor(
        private router: Router,
        private calculator: CalculatorService,
        private alert: AlertController
    ) {
        this.refinanceModeHandler = this.refinanceModeHandler.bind(this);
    }

    ngOnInit() {
        this.setupFormControls();
        this.refinanceModeSubscription$ = this.refinanceModeSwitch.subscribe(this.refinanceModeHandler);
    }

    ngOnDestroy() {
        if (this.refinanceModeSubscription$) {
            this.refinanceModeSubscription$.unsubscribe();
        }
    }

    handleToggleChange(event: any) {
        this.refinanceModeSwitch.emit(event);
        this.refinanceChecked = event.detail.checked
    }

    refinanceModeHandler(event: any) {
        if (event === 'disable') {
            this.disableRefinanceSwitch = true;
            this.refinanceChecked = false;
        } else if (event === 'finance') {
            this.refinanceChecked = true;
            this.disableRefinanceSwitch = false;
        }
    }

    /**
     * Adds the form controls of the given fields to the form group.
     */
    setupFormControls() {
        this.sections.forEach((section: ICalculatorSection) => {
            section.fields.forEach((field: ICalculatorField) => {
                if (field.formControl) {
                    this.form.addControl(field.id, field.formControl);
                }
            });
        });
    }

    inputFocus(field: ICalculatorField) {
        // If value has been changed before
        if (field.formControl?.dirty) {
            // Was last interaction more than 5 minutes ago?
            if ((Number(new Date()) - Number(this.lastInteraction)) >= 300000) {
                this.disregardNextBlur = true;
                void this.showStillOnSamePropertyModal();
            }
        }

        this.lastInteraction = new Date();
    }

    async showStillOnSamePropertyModal() {
        const buttons = [
            {
                text: 'No',
                role: 'cancel',
                cssClass: 'danger',
                handler: () => {
                    this.router.navigate(['/dashboard/properties'], {
                        queryParamsHandling: 'merge'
                    });
                }
            },
            {
                text: 'Yes',
            }
        ];

        const alert = await this.alert.create({
            header: 'Still on correct property?',
            message: 'Are you still entering numbers for this property address? If not click No and you will be returned to the property list.',
            buttons: buttons
        });

        await alert.present();
    }

    inputBlur(field: ICalculatorField) {
        if (this.disregardNextBlur) {
            this.disregardNextBlur = false;
            return;
        }

        // If value empty do nothing
        if (!field.formControl?.value || field.formControl.value === '') {
            field.inputValue = null;
            this.valueChange();
            return;
        }

        let cleanValue = this.cleanUpValue(field.formControl.value);

        // If cleaned up input is not a number reset to empty
        if (Number.isNaN(Number(cleanValue))) {
            field.inputValue = null;
            field.formControl.setValue('');
            this.valueChange();
            return;
        }

        // Convert percentage input to decimal percent.
        if (field.unit === CALCULATOR_FIELD_UNITS.PERCENT) {
            cleanValue = cleanValue / 100;
        }

        const styledValue = this.addStylingToInput(cleanValue, field.unit as CALCULATOR_FIELD_UNITS);

        field.inputValue = Number(cleanValue);
        field.formControl.setValue(styledValue);

        this.valueChange();
    }


    /**
     * Applies the proper Currency or % styling to the clean value.
     * @param cleanValue A number value.
     * @param unit The unit of the field
     */
    addStylingToInput(cleanValue: number, unit: CALCULATOR_FIELD_UNITS) {
        if (unit === CALCULATOR_FIELD_UNITS.DOLLAR) {
            return this.calculator.formatAsUSD(cleanValue);
        } else if (unit === CALCULATOR_FIELD_UNITS.PERCENT) {
            return this.calculator.formatAsPercent(cleanValue);
        } else {
            // No styling can be applied for this unit
            return cleanValue;
        }
    }

    /**
     * Removes dollar sign ($), percent sign (%), and comma (,) from value.
     * @param styledValue A value that is potentially styled.
     */
    cleanUpValue(styledValue: any) {
        return styledValue.replace(/[%$,]/gi, '');
    }
}
